import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ServiceCard from "@/components/ServiceCard";
import { ArrowRight, CheckCircle2, TrendingUp, Sparkles } from "lucide-react";

export default function Home() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showcase, setShowcase] = useState({ items: [], total_monthly: 0, total_yearly: 0 });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/showcase").then((r) => setShowcase(r.data));
  }, []);

  const featured = services.filter((s) => s.featured).slice(0, 6);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden mds-grain">
        <div className="mds-container relative z-10 pt-16 sm:pt-24 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 mds-fade-up">
            <span className="mds-badge mds-badge-primary mb-6">
              <Sparkles size={14} /> Real-world money-saving info
            </span>
            <h1 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Save <span style={{ color: "#6B8F73" }}>£220 every month</span> with tools you'd already use.
            </h1>
            <p className="mt-6 text-lg text-[#4A5D54] max-w-xl leading-relaxed">
              I'm Matt. I test every cashback site, reward app, card tool and survey panel in the UK — then share the ones that actually pay. Here are the ones actually worth your time.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/directory" className="mds-btn mds-btn-primary" data-testid="hero-cta-directory">
                Browse the directory <ArrowRight size={18} />
              </Link>
              <Link to="/showcase" className="mds-btn mds-btn-secondary" data-testid="hero-cta-showcase">
                See my £220/mo breakdown
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 text-sm text-[#4A5D54]">
              {[
                { icon: CheckCircle2, label: "13+ services reviewed" },
                { icon: TrendingUp, label: `£${showcase.total_monthly.toFixed(0)}/mo real earnings` },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <f.icon size={16} className="text-[#84A98C]" strokeWidth={2} />
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-lg border border-[#E5E3DB]">
              <img
                src="https://images.unsplash.com/photo-1566753323558-f4e0952af115?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMG1hbiUyMHBvcnRyYWl0JTIwc21pbGluZyUyMG5hdHVyYWx8ZW58MHx8fHwxNzgzMzQ2ODkyfDA&ixlib=rb-4.1.0&q=85"
                alt="Matt"
                className="w-full h-[440px] object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-[#E5E3DB] p-5 shadow-lg hidden sm:block">
              <div className="text-xs uppercase tracking-wide text-[#7A8C84]">This year</div>
              <div className="font-num font-semibold text-3xl text-[#1C2321]">£{showcase.total_yearly.toFixed(0)}</div>
              <div className="text-xs text-[#4A5D54]">saved & earned</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mds-container py-16">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
          <div>
            <span className="mds-badge">Explore</span>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl mt-3">Pick a category</h2>
          </div>
          <Link to="/directory" className="mds-link inline-flex items-center gap-1" data-testid="see-all-categories">
            See everything <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((c, i) => (
            <Link
              key={c.id}
              to={`/directory?cat=${c.id}`}
              className="mds-card group"
              data-testid={`category-${c.id}`}
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <div className="mds-fade-up">
                <div className="font-num text-4xl font-semibold" style={{ color: "#84A98C" }}>{c.count.toString().padStart(2, "0")}</div>
                <div className="mt-4 font-heading font-semibold text-lg group-hover:text-[#6B8F73] transition-colors">
                  {c.label}
                </div>
                <p className="text-sm text-[#4A5D54] mt-1 leading-relaxed">{c.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="mds-container py-16">
        <div className="mb-10">
          <span className="mds-badge mds-badge-accent">Matt's top picks</span>
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl mt-3">Start with these six</h2>
          <p className="text-[#4A5D54] mt-3 max-w-2xl leading-relaxed">
            If you set up nothing else, set up these. They cover 80% of the earnings for a fraction of the effort.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="mds-container py-12">
        <div
          className="rounded-3xl p-8 sm:p-12 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #84A98C 0%, #6B8F73 100%)" }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center relative z-10">
            <div className="lg:col-span-8">
              <h3 className="font-heading font-semibold text-white text-2xl sm:text-3xl leading-tight">
                Save up to £220 a month?
              </h3>
              <p className="text-white/85 mt-3 max-w-xl">
                A full breakdown of every app, what it pays me and how much time it takes.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/showcase"
                className="mds-btn bg-white text-[#1C2321] hover:bg-[#F0EFEA]"
                data-testid="cta-showcase-strip"
              >
                See the breakdown <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
